import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";

const initialState = {
  balance: null,
  userMatchStack: null,
  openBets: null,
  unmatchedBets: null,
  stakeSettings: [],
  stakeSettingsLoaded: false,
  loading: false,
  initialLoad: true,
  error: null,
};



export const updateStakeSettingsAsync = createAsyncThunk(
  "bet/updateStakeSettings",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/user/updateMatchStack", payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update stake settings",
      );
    }
  },
);

export const getStakeSettingsAsync = createAsyncThunk(
  "bet/getStakeSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/user/getUserMatchStack", {});
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get stake settings",
      );
    }
  },
);

export const fetchUserMatchStackAsync = createAsyncThunk(
  "bet/fetchUserMatchStack",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/user/getUserMatchStack");
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user match stack",
      );
    }
  },
);
export const getBalanceCRefAsync = createAsyncThunk(
  "bet/getBalanceCRef",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/user/getBalanceCRef");
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch balance c ref",
      );
    }
  },
);


export const makeFancyBetAsync = createAsyncThunk(
  "bet/makeFancyBet",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/bet/saveFancyBet", payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to make fancy bet",
      );
    }
  },
);


export const makeMarketBetAsync = createAsyncThunk(
  "bet/makeMarketBet",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/bet/saveBet", payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to make fancy bet",
      );
    }
  },
);


export const fetchOpenBetsAsync = createAsyncThunk(
  "bet/fetchOpenBets",
  async (_, { rejectWithValue }) => {
    try {
      const payload = {
        limit: 100,
        page: 1,
        search: { delete_status: 0, is_matched: 1 },
      };
      const response = await axiosInstance.post("/bet/openBets", payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch open bets",
      );
    }
  },
);


export const fetchUnmatchedBets = createAsyncThunk(
  "bet/fetchUnmatchedBets",
  async ({ eventId, sportId } = {}, { rejectWithValue }) => {
    try {
      const data = {
        limit: 100,
        page: 1,
        search:
          Number(sportId) === 7 || Number(sportId) === 4339
            ? { delete_status: 0, market_id: eventId }
            : { delete_status: 0, match_id: eventId },
      };
      const res = await axiosInstance.post("/bet/unmatchedBets", data);
      return res?.data?.[0]?.data ?? [];
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to fetch unmatched bets",
      );
    }
  },
);

export const placeMatchOddsBetAsync = createAsyncThunk(
  "bet/placeMatchOddsBet",
  async (betData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/bet/saveBet", betData);
      if (!response?.status) {
        return rejectWithValue(
          response?.msg || "Failed to place match odds bet",
        );
      }
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to place match odds bet",
      );
    }
  },
);



export const calculateCashout = (market, positions, options = {}) => {
  const debug = Boolean(options?.debug);
  const fail = (reason, extra = {}) => {
    if (debug) {
      // eslint-disable-next-line no-console
      console.debug("[cashout] unavailable:", reason, extra);
    }
    return null;
  };

  const normalizedMarket = {
    ...market,
    market_id: market?.market_id ?? market?.marketId ?? market?.id,
    runners: Array.isArray(market?.runners)
      ? market.runners.map((r) => ({
          ...r,
          selectionId: r?.selectionId ?? r?.selection_id ?? r?.id ?? r?._id,
          ex: {
            ...r?.ex,
            availableToLay:
              r?.ex?.availableToLay ??
              r?.ex?.available_to_lay ??
              r?.availableToLay ??
              r?.lay ??
              r?.lays ??
              [],
          },
        }))
      : [],
  };

  const normalizedPositions = Array.isArray(positions)
    ? positions
    : Array.isArray(positions?.data)
      ? positions.data
      : Array.isArray(positions?.positions)
        ? positions.positions
        : [];

  // === Strict production logic from source repo starts here ===
  if (
    !normalizedMarket ||
    !normalizedMarket.runners ||
    normalizedMarket.runners.length !== 2
  ) {
    return fail("INVALID_MARKET");
  }
  if (!normalizedPositions || normalizedPositions.length === 0) {
    return fail("NO_POSITIONS");
  }

  const getExposure = (selectionId) => {
    const pos = normalizedPositions.find(
      (p) =>
        String(p?.selection_id) === String(selectionId) ||
        String(p?.SelectionId) === String(selectionId) ||
        String(p?.selectionId) === String(selectionId),
    );
    return pos ? Number(pos?.win_loss ?? pos?.winLoss ?? pos?.user_pl ?? 0) : 0;
  };

  const runner0 = normalizedMarket.runners[0];
  const runner1 = normalizedMarket.runners[1];

  const exp0 = getExposure(runner0.selectionId);
  const exp1 = getExposure(runner1.selectionId);

  let greenAmount = 0;
  let redAmount = 0;
  let targetRunner = null;
  let targetOdds = 0;

  if (exp0 > 0 && exp1 > 0) {
    if (exp0 > exp1) {
      greenAmount = exp0;
      redAmount = exp1;
      targetRunner = runner0;
      targetOdds = runner0?.ex?.availableToLay?.[0]?.price;
    } else {
      greenAmount = exp1;
      redAmount = exp0;
      targetRunner = runner1;
      targetOdds = runner1?.ex?.availableToLay?.[0]?.price;
    }
  } else if (exp0 >= 0) {
    greenAmount = exp0;
    targetRunner = runner0;
    targetOdds = runner0?.ex?.availableToLay?.[0]?.price;
    redAmount = exp1;
  } else if (exp1 >= 0) {
    greenAmount = exp1;
    targetRunner = runner1;
    targetOdds = runner1?.ex?.availableToLay?.[0]?.price;
    redAmount = exp0;
  } else {
    return fail("BOTH_EXPOSURES_NEGATIVE", { exp0, exp1 });
  }

  if (!targetRunner || !targetOdds) return fail("NO_TARGET_OR_ODDS", { targetOdds });

  const rawStake = (greenAmount - redAmount) / Number(targetOdds);
  const stake = Math.floor(rawStake);
  if (stake <= 0) return fail("STAKE_NON_POSITIVE", { rawStake, stake });

  const payload = {
    is_back: 0,
    market_id: normalizedMarket.market_id,
    odds: Number(targetOdds),
    selection_id: targetRunner.selectionId,
    stack: stake,
  };

  let projected0 = 0;
  if (targetRunner.selectionId == runner0.selectionId) {
    const liability = stake * (targetOdds - 1);
    projected0 = exp0 - liability;
  } else {
    projected0 = exp0 + stake;
  }

  let projected1 = 0;
  if (targetRunner.selectionId == runner1.selectionId) {
    const liability = stake * (targetOdds - 1);
    projected1 = exp1 - liability;
  } else {
    projected1 = exp1 + stake;
  }

  const projectedProfit = projected0 < projected1 ? projected0 : projected1;

  const runnersSummary = {
    before: [
      {
        name: runner0?.runner_name ?? runner0?.selection_name ?? runner0?.name ?? "",
        pl: exp0,
      },
      {
        name: runner1?.runner_name ?? runner1?.selection_name ?? runner1?.name ?? "",
        pl: exp1,
      },
    ],
    after: [
      {
        name: runner0?.runner_name ?? runner0?.selection_name ?? runner0?.name ?? "",
        pl: projected0,
      },
      {
        name: runner1?.runner_name ?? runner1?.selection_name ?? runner1?.name ?? "",
        pl: projected1,
      },
    ],
    averageAfter: (projected0 + projected1) / 2,
  };

  return {
    payload,
    profit: projectedProfit,
    runners: runnersSummary,
  };
};

/**
 * Legacy exports for compatibility if needed,
 * but we should switch to the single unified function above.
 */
export const generateCashoutPayload = (market, positions) => {
  const res = calculateCashout(market, positions);
  return res ? res.payload : null;
};

export const calculateCashoutProfit = (market, positions) => {
  const res = calculateCashout(market, positions);
  return res ? res.profit : null;
};



const betSlice = createSlice({
  name: "bet",
  initialState,
  reducers: {
    clearBalance: (state) => {
      state.balance = null;
      state.userMatchStack = null;
      state.openBets = null;
      state.error = null;
      state.initialLoad = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserMatchStackAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchUserMatchStackAsync.fulfilled, (state, action) => {
        state.userMatchStack = action.payload;
        state.error = null;
      })
      .addCase(fetchUserMatchStackAsync.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchOpenBetsAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchOpenBetsAsync.fulfilled, (state, action) => {
        state.openBets = action.payload;
        state.error = null;
      })
      .addCase(fetchOpenBetsAsync.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchUnmatchedBets.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchUnmatchedBets.fulfilled, (state, action) => {
        state.unmatchedBets = action.payload;
        state.error = null;
      })
      .addCase(fetchUnmatchedBets.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(getBalanceCRefAsync.fulfilled, (state, action) => {
        state.balance = action.payload;
      })
      .addCase(getBalanceCRefAsync.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(getStakeSettingsAsync.fulfilled, (state, action) => {
        const raw = action.payload;
        const fromMatchStack =
          (raw && raw.match_stack) ||
          (raw && raw.data && raw.data.match_stack);
        const arr = Array.isArray(fromMatchStack)
          ? fromMatchStack
          : Array.isArray(raw)
            ? raw
            : [];
        state.stakeSettings = arr;
        state.stakeSettingsLoaded = true;
      })
      .addCase(getStakeSettingsAsync.rejected, (state, action) => {
        state.stakeSettingsLoaded = true;
        state.error = action.payload;
      })
      .addCase(makeFancyBetAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(makeFancyBetAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(makeFancyBetAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(makeMarketBetAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(makeMarketBetAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(makeMarketBetAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearBalance } = betSlice.actions;
export default betSlice.reducer;
